'use client';

import { MinerPredictionsGraph } from '@/components/synth-dashboard/MinerPredictionsGraph';
import { PriceDistribution } from '@/components/synth-dashboard/PriceDistribution';
import { GraphContainer } from '@/components/synth-dashboard/GraphContainer';
import { BrainIcon } from '@/icons';
import { usePricePredictions, Series } from '@/hooks/synth/usePricePredictions';
import { useState, useEffect } from 'react';
import { Spinner } from '@/components';
import { SelectWithSearch, SelectOption } from '@/components';
import { useLeaderboard } from '@/hooks/synth/useLeaderboard';
import { SupportedInsightsAsset } from '@/app/api/constants';
import { AssetDropdown } from '@/components/insights/AssetDropdown';

export const PricePredictions = ({ allowSelectMiner = true }: { allowSelectMiner?: boolean }) => {
  const [asset, setAsset] = useState<SupportedInsightsAsset>(SupportedInsightsAsset.BTC);
  const { data: leaderboard } = useLeaderboard();
  const [selectedOption, setSelectedOption] = useState<SelectOption<number>>();
  const { data: series, isLoading: isSeriesLoading } = usePricePredictions(
    { enabled: selectedOption?.value != null },
    asset,
    selectedOption?.value
  );
  const [selectedTime, setSelectedTime] = useState<Date>();
  const [prevSeries, setPrevSeries] = useState<Series[]>();

  useEffect(() => {
    if (series) {
      // Only update if series has actually changed
      if (JSON.stringify(series) !== JSON.stringify(prevSeries)) {
        setPrevSeries(series);
      }
    }
  }, [series, prevSeries]);

  useEffect(() => {
    if (leaderboard?.[0]?.neuron_uid) {
      setSelectedOption({ label: 'Top miner', value: leaderboard[0].neuron_uid });
    }
  }, [leaderboard]);

  const displaySeries = isSeriesLoading ? prevSeries : series;

  return (
    <GraphContainer
      header={
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-y-4">
          <div className="flex items-center gap-x-2 text-white font-medium text-sans text-sm">
            <BrainIcon className="w-4 h-4" />
            <p>{asset} Price Predictions</p>
          </div>
          <div className="flex items-center gap-x-2">
            <AssetDropdown showLabel={false} asset={asset} onSelect={setAsset} />
            {allowSelectMiner && selectedOption && (
              <SelectWithSearch
                options={leaderboard?.map((miner) => ({ label: `${miner.neuron_uid}`, value: miner.neuron_uid })) || []}
                selectedOption={selectedOption}
                onSelect={setSelectedOption}
                searchPlaceholder="Miner ID"
              />
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col lg:flex-row items-start justify-between gap-x-4">
        {!prevSeries ? (
          <div className="w-full flex justify-center items-center h-[400px]">
            <Spinner />
          </div>
        ) : (
          <>
            {displaySeries && (
              <>
                <MinerPredictionsGraph series={displaySeries} onSelectTime={setSelectedTime} />
                <PriceDistribution series={displaySeries} selectedTime={selectedTime} />
              </>
            )}
          </>
        )}
      </div>
    </GraphContainer>
  );
};
